import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../../constants/common'
import { SUBMENU_DEFAULT_VALUE } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { useRegisterReload, useScreen } from '../../../hooks'
import { BREADCRUMB_VARIANT } from '../../Breadcrumbs'
import Button, { BUTTON_HEIGHT_40 } from '../../Button'
import {
  DocumentTableRow,
  EXCLUDE_FILTERS,
  getDocumentsPermanentFilters,
  getDraggableRowProps,
  getOnClickProps,
} from '../../Document'
import { FileDirectoryBreadcrumbs } from '../../File'
import { FilesPreviewGallery } from '../../FilesPreviewGallery'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import { FilterDivider } from '../../FilterDivider'
import { PageActions } from '../../Page'
import { PageEmptyState } from '../../PageEmptyState'
import { Pagination } from '../../Pagination'
import {
  getSelectedSize,
  getTableResetSortingButton,
  Table,
  TableConfigModal,
  TableControlBar,
  TableControlButtons,
  TableDroppableBody,
  TableMassAction,
} from '../../Table'
import { TablePagination } from '../../TablePagination'

import {
  ENTITY_TAB_DOCUMENTS_DEFAULT_FILTER_BAR_PROPS,
  QA_NAME_ENTITY_TAB_DOCUMENTS,
  QA_NAME_ENTITY_TAB_DOCUMENTS_ADD_FOLDER,
  QA_NAME_ENTITY_TAB_DOCUMENTS_UPLOAD,
} from './EntityTabDocuments.constants'
import { useEntityTabDocumentsPage } from './EntityTabDocuments.hooks'

import * as styles from './EntityTabDocuments.module.css'

import DocumentsIcon from '../../../icons/doc.svg'
import PlusIcon from '../../../icons/plus.svg'
import UploadIcon from '../../../icons/upload-1.svg'

function EntityTabDocuments({
  UploadButton,
  breadcrumbRootItems,
  breadcrumbStorageItemId,
  className,
  contextData,
  entity,
  filterBarProps: filterBarPropsIn = EMPTY_OBJECT,
  filterTriggerVariant = FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON,
  folderFormProps,
  getDownloadApiParams,
  getFilterBarApiUrl,
  getItemActions,
  getMassActions,
  isPreview,
  objectId,
  outboundUuid,
  qaNameTable = QA_NAME_ENTITY_TAB_DOCUMENTS,
  renderUploadDestinationModal,
  rootDirectoryId,
  scope,
  sendViaEmailProps,
  sub = SUBMENU_DEFAULT_VALUE,
  uploadButtonProps = EMPTY_OBJECT,
  uploadFileProps,
  onAfterUpload,
  onLoadCounters,
  onRegisterReload,
  onSendViaEmail,
}) {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const {
    activeFileIndex,
    availableFilters,
    breadcrumbs,
    checkIsItemSelected,
    columns,
    exclude,
    filterBarApiUrl,
    filterValues,
    getRootDnDProps,
    getRowItemActions,
    getUploadFileInputProps,
    handleChangeShouldReloadFilters,
    handleClickFile,
    handleClickFolder,
    handleClickUploadFile,
    handleCloseFilePreview,
    handleConfigOpen,
    handleDragEnd,
    handleDragOver,
    handleExclude,
    handleOpenCreation,
    handleReload,
    handleReloadList,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSetColumns,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    handleUpdateConfig,
    hoveredRowId,
    isAllSelected,
    isConfigOpen,
    isInitialLoadSettled,
    isLoading,
    isRemoveSelection,
    items,
    massActions,
    modals,
    ordering,
    pageScrollWrapperRef,
    pagination,
    refreshButtonProps,
    renderOverlay,
    requiredColumns,
    scope: filesPreviewScope,
    selectAllPages,
    selected,
    setConfigOpen,
    setItemAction,
    setMassAction,
    shouldReloadFilters,
    showDocumentActions,
    showFilterBar,
    tableItems,
  } = useEntityTabDocumentsPage({
    breadcrumbRootItems,
    breadcrumbStorageItemId,
    contextData,
    entity,
    folderFormProps,
    getDownloadApiParams,
    getFilterBarApiUrl,
    getItemActions,
    getMassActions,
    isPreview,
    objectId,
    outboundUuid,
    renderUploadDestinationModal,
    rootDirectoryId,
    scope,
    sendViaEmailProps,
    sub,
    uploadFileProps,
    onAfterUpload,
    onLoadCounters,
    onSendViaEmail,
  })

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadList,
    onRegisterReload,
  })

  const isEmptyState = !isLoading && tableItems.length === 0

  const showTopActions = showDocumentActions && !isEmptyState

  function getMergedUploadButtonProps() {
    const {
      controlProps: userControlProps,
      onSelectItem: userOnSelectItem,
      ...rest
    } = uploadButtonProps

    if (!UploadButton) {
      return EMPTY_OBJECT
    }

    return {
      ...rest,
      controlProps: {
        ...userControlProps,
        buttonProps: {
          ...userControlProps?.buttonProps,
          ...(isMobileScreenSize ? { text: EMPTY_STRING } : null),
          isSkeleton: isLoading,
        },
      },
      onSelectItem: option => {
        userOnSelectItem?.(option)

        if (option?.value) {
          handleClickUploadFile()
        }
      },
    }
  }

  const mergedUploadButtonProps = getMergedUploadButtonProps()

  function getEmptyStateActions() {
    const uploadAction = UploadButton
      ? {
          ...mergedUploadButtonProps,
          Component: UploadButton,
          controlProps: {
            ...mergedUploadButtonProps.controlProps,
            Button: Button.White,
            buttonProps: {
              ...mergedUploadButtonProps.controlProps?.buttonProps,
              height: BUTTON_HEIGHT_40,
              isGrey: true,
            },
          },
          name: 'upload',
          qaName: QA_NAME_ENTITY_TAB_DOCUMENTS_UPLOAD,
        }
      : {
          Icon: UploadIcon,
          name: 'upload',
          qaName: QA_NAME_ENTITY_TAB_DOCUMENTS_UPLOAD,
          text: t('DocumentsTable.UploadFileBtnKey'),
          onClick: handleClickUploadFile,
        }

    const folderAction = {
      Icon: PlusIcon,
      name: 'add-folder',
      qaName: QA_NAME_ENTITY_TAB_DOCUMENTS_ADD_FOLDER,
      text: t('DocumentsTable.AddNewFolderBtnKey'),
      onClick: handleOpenCreation,
    }

    return items?.parent?.permissions?.can_create
      ? [uploadAction, folderAction]
      : undefined
  }

  const emptyStateActions = getEmptyStateActions()

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div
        className={classNames(styles.root, className)}
        ref={pageScrollWrapperRef}
      >
        {breadcrumbs?.length > 0 || isLoading ? (
          <div className={styles.breadcrumbsRow}>
            <FileDirectoryBreadcrumbs
              className={classNames(styles.breadcrumbs, {
                [styles.breadcrumbsLoading]: isLoading,
              })}
              isLoading={isLoading}
              items={breadcrumbs}
              variant={BREADCRUMB_VARIANT.TAB}
            />
          </div>
        ) : null}
        <div {...getRootDnDProps()} className={styles.tableWrapper}>
          <input {...getUploadFileInputProps()} />
          {showFilterBar || showTopActions ? (
            <div
              className={classNames(styles.toolbar, {
                [styles.toolbarEnd]: showTopActions && !showFilterBar,
              })}
            >
              {showFilterBar ? (
                <FilterBar
                  {...ENTITY_TAB_DOCUMENTS_DEFAULT_FILTER_BAR_PROPS}
                  {...filterBarPropsIn}
                  responsive
                  apiUrl={filterBarApiUrl}
                  availableFilters={availableFilters}
                  className={styles.filterBar}
                  entity={entity}
                  excludeFilters={EXCLUDE_FILTERS}
                  filterTriggerVariant={filterTriggerVariant}
                  filterValues={filterValues}
                  getPermanentFilters={getDocumentsPermanentFilters}
                  isSkeleton={isLoading}
                  objectId={objectId}
                  ordering={ordering}
                  pagination={{ per_page: pagination?.pageSize }}
                  setFilterValues={handleSetFilterValues}
                  shouldReloadValues={shouldReloadFilters}
                  sub={sub}
                  onChangeShouldReloadValues={handleChangeShouldReloadFilters}
                  onReload={handleReload}
                />
              ) : null}
              {showTopActions ? (
                <div className={styles.toolbarActions}>
                  {UploadButton ? (
                    <UploadButton
                      {...mergedUploadButtonProps}
                      qaName={QA_NAME_ENTITY_TAB_DOCUMENTS_UPLOAD}
                    />
                  ) : (
                    <Button.Text
                      beforeIconProps={{ Icon: UploadIcon }}
                      isSkeleton={isLoading}
                      qaName={QA_NAME_ENTITY_TAB_DOCUMENTS_UPLOAD}
                      text={
                        isMobileScreenSize
                          ? EMPTY_STRING
                          : t('DocumentsTable.UploadFileBtnKey')
                      }
                      onClick={handleClickUploadFile}
                    />
                  )}
                  <Button.Text
                    beforeIconProps={{ Icon: PlusIcon }}
                    isSkeleton={isLoading}
                    qaName={QA_NAME_ENTITY_TAB_DOCUMENTS_ADD_FOLDER}
                    text={
                      isMobileScreenSize
                        ? EMPTY_STRING
                        : t('DocumentsTable.AddNewFolderBtnKey')
                    }
                    onClick={handleOpenCreation}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {isEmptyState ? (
            <PageEmptyState
              EntityIcon={DocumentsIcon}
              actions={emptyStateActions}
              className={styles.emptyState}
              text={t('DocumentsTable.NoDataKey')}
            />
          ) : (
            <>
              <TableControlBar>
                <TableMassAction
                  responsive
                  actions={massActions}
                  isAllSelected={isAllSelected}
                  isLoading={isLoading}
                  isRemoveSelection={isRemoveSelection}
                  selectedSize={getSelectedSize({
                    exclude,
                    pagination,
                    selectAllPages,
                    selected,
                  })}
                  setMassAction={setMassAction}
                  onSelectAll={handleSelectAll}
                />
                <PageActions isLoading={isLoading}>
                  <TableControlButtons
                    DensityButton={null}
                    ResetSortingButton={getTableResetSortingButton(ordering)}
                    configButtonProps={{ onClick: handleConfigOpen }}
                    refreshButtonProps={refreshButtonProps}
                    resetSortingProps={{ onClick: handleResetOrdering }}
                  />
                  <FilterDivider />
                  <Pagination
                    responsive
                    value={pagination}
                    onChange={handleSetPagination}
                    onChangeSize={handleSetSize}
                  />
                </PageActions>
              </TableControlBar>
              <Table
                TableBody={TableDroppableBody}
                TableRow={DocumentTableRow}
                bodyProps={{
                  droppableProps: {
                    overlayProps: {
                      style: { cursor: 'grabbing', pointerEvents: 'none' },
                    },
                    renderOverlay,
                    onDragEnd: handleDragEnd,
                    onDragOver: handleDragOver,
                  },
                  items: items?.items,
                }}
                checkIsItemSelected={checkIsItemSelected}
                columns={columns}
                containerRef={pageScrollWrapperRef}
                headerRowProps={{
                  ordering,
                  onApply: handleUpdateConfig,
                  onSetColumns: handleSetColumns,
                  onSetOrdering: handleSetOrdering,
                }}
                isLoading={isLoading}
                items={tableItems}
                qaName={qaNameTable}
                rowProps={{
                  droppableProps: getRootDnDProps(),
                  exclude,
                  getActions: getRowItemActions,
                  getDraggableRowProps,
                  getOnClickProps,
                  hideCheckbox: massActions.length === 0,
                  hoveredId: hoveredRowId,
                  isSelectedAll: selectAllPages,
                  selected,
                  onCallAction: setItemAction,
                  onClick: handleClickFile,
                  onClickFolder: handleClickFolder,
                  onSelect: selectAllPages ? handleExclude : handleSelect,
                }}
              />
              <TablePagination
                responsive
                isSkeleton={isLoading}
                value={pagination}
                onChange={handleSetPagination}
              />
            </>
          )}
          {isConfigOpen ? (
            <TableConfigModal
              columns={columns}
              entity={entity}
              params={{ outbound_uuid: outboundUuid }}
              requiredColumns={requiredColumns}
              setColumns={handleSetColumns}
              setConfigOpen={setConfigOpen}
              onApply={handleUpdateConfig}
            />
          ) : null}
          {modals.map(([id, element]) => (
            <Fragment key={id}>{element}</Fragment>
          ))}
          <FilesPreviewGallery
            files={items?.items}
            initActive={activeFileIndex}
            scope={filesPreviewScope}
            subject={objectId}
            onClose={handleCloseFilePreview}
          />
        </div>
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabDocuments
